// components/QuotationPDFExport.jsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  PictureAsPdf,
  Download,
  Close,
  Preview,
  Print,
  CloudDownload,
  Business,
  Person
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const QuotationPDFExport = ({ quotationData, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Calculate totals with GST per item
const calculateTotals = () => {
  if (!quotationData) return null;

  let itemsTotal = 0;
  let cgstTotal = 0;
  let sgstTotal = 0;
  
  quotationData.items?.forEach(item => {
    const qty = parseFloat(item.qty || 1);
    const unitPrice = parseFloat(item.unit_price || 0);
    const cgstPercent = parseFloat(item.cgst || 0);
    const sgstPercent = parseFloat(item.sgst || 0);
    
    const itemSubtotal = qty * unitPrice;
    itemsTotal += itemSubtotal;
    
    // Calculate CGST and SGST for this item
    const itemCGST = itemSubtotal * (cgstPercent / 100);
    const itemSGST = itemSubtotal * (sgstPercent / 100);
    cgstTotal += itemCGST;
    sgstTotal += itemSGST;
  });
  
  const additionalCosts = [
    'diagnostics_fee',
    'labour_cost',
    'parts_estimate',
    'testing_and_commissioning',
    'packaging_and_shipping',
  ].reduce((sum, field) => 
    sum + (parseFloat(quotationData[field]) || 0), 0);
  
  const subtotal = itemsTotal + additionalCosts;
  const discountAmount = subtotal * (parseFloat(quotationData.discount_percent || 0) / 100);
  const afterDiscount = subtotal - discountAmount;
  
  // Remove additional taxes calculation since we only have CGST/SGST per item
  const gstTotal = cgstTotal + sgstTotal;
  const grandTotal = afterDiscount + gstTotal;
  
  return { 
    itemsTotal, 
    subtotal, 
    discountAmount, 
    afterDiscount,
    cgstTotal,
    sgstTotal,
    gstTotal,
    grandTotal,
    additionalCosts
  };
};

  const totals = calculateTotals();

  // Format currency based on currency type
  const formatCurrency = (amount) => {
    if (!amount) amount = 0;
    
    if (quotationData.currency === 'INR') {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } else if (quotationData.currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } else if (quotationData.currency === 'EUR') {
      return new Intl.NumberFormat('en-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
    
    return `${quotationData.currency} ${amount.toFixed(2)}`;
  };

  // Generate PDF using jsPDF
const generatePDF = async () => {
  setLoading(true);
  setError(null);
  
  try {
    // Create a new jsPDF instance with proper encoding
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    // Set default font with proper encoding
    pdf.setFont('helvetica');
    pdf.setFontSize(10);

    // Helper function to format numbers correctly
    const formatNumber = (num) => {
      if (!num && num !== 0) return '0.00';
      const number = parseFloat(num);
      if (isNaN(number)) return '0.00';
      
      // Handle different currency formats
      if (quotationData.currency === 'INR') {
        return new Intl.NumberFormat('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(number);
      } else if (quotationData.currency === 'USD' || quotationData.currency === 'EUR') {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(number);
      }
      return number.toFixed(2);
    };

    // Helper function to get currency symbol
    const getCurrencySymbol = () => {
      switch(quotationData.currency) {
        case 'INR': return '₹';
        case 'USD': return '$';
        case 'EUR': return '€';
        case 'GBP': return '£';
        default: return '';
      }
    };

    const currencySymbol = getCurrencySymbol();

    // Helper function to format currency
    const formatCurrency = (amount) => {
      return `${currencySymbol}${formatNumber(amount)}`;
    };

    // Helper function to safely add text with proper encoding
    const addText = (text, x, y, options = {}) => {
      if (!text) text = '';
      // Convert to string and clean
      const cleanText = String(text)
        .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
        .replace(/[*]/g, '') // Remove asterisks
        .trim();
      
      pdf.text(cleanText, x, y, options);
    };

    // Helper function to check if we need a new page
    const checkPageBreak = (neededHeight) => {
      const pageHeight = 297; // A4 height in mm
      const marginBottom = 20; // Leave margin at bottom
      
      if (yPos + neededHeight > pageHeight - marginBottom) {
        pdf.addPage();
        yPos = 20;
        return true;
      }
      return false;
    };

    // Set initial position
    let yPos = 20;

    // Company Header with proper encoding
// Company Header with proper encoding
pdf.setFillColor(41, 128, 185);
pdf.rect(10, 10, 190, 25, 'F');  // increased height to fit stacked details

pdf.setTextColor(255, 255, 255);

// ---------------- LEFT SIDE (Address, Email, Contact, GST) ----------------
pdf.setFont('helvetica', 'bold');
pdf.setFontSize(16);

// Company Name - Bold and larger
addText(
  quotationData.company_info?.name || '',
  15,
  17
);

// Address - Normal font
pdf.setFont('helvetica', 'normal');
pdf.setFontSize(10);
const addressLines = pdf.splitTextToSize(
  quotationData.company_info?.address || '',
  85  // Width for address wrapping
);

addressLines.forEach((line, index) => {
  addText(line, 15, 23 + (index * 4));
});

// ---------------- RIGHT SIDE (Contact Details) ----------------
let rightX = 135;  // Starting X position for right column
let rightY = 17;   // Starting Y position

pdf.setFont('helvetica', 'normal');
pdf.setFontSize(10);

const rightInfo = [
  { label: 'Email:', value: quotationData.company_info?.email || '' },
  { label: 'Contact:', value: quotationData.company_info?.contact || '' },
  { label: 'GST/VAT:', value: quotationData.company_info?.gst_vat || '' }
];

rightInfo.forEach((item, index) => {
  if (item.value) {
    // Draw label in slightly bolder text
    pdf.setFont('helvetica', 'bold');
    addText(item.label, rightX, rightY + (index * 6));
    
    // Draw value
    pdf.setFont('helvetica', 'normal');
    addText(item.value, rightX + 20, rightY + (index * 6));
  }
});

// ---------------- QUOTATION TITLE ----------------
pdf.setFont('helvetica', 'bold');
pdf.setFontSize(16);
pdf.setTextColor(0, 0, 0);  // Black text

pdf.text('QUOTATION', 105, 42, { align: 'center' });

// Underline
pdf.setDrawColor(0, 0, 0);
pdf.line(70, 44, 140, 44);



    // Reset color for content lineHeight
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);
    

    // Bill To section with GST
// FIXED START POSITION for both sections
let billStartY = 50;
let detailsStartY = 50;
    pdf.setFont('helvetica', 'bold');
    addText('Bill To:', 10, billStartY);
    pdf.setFont('helvetica', 'normal');
let billY = billStartY + 5;
    const clientInfo = [
      quotationData.client_info?.name || '',
      quotationData.client_info?.address || '',
      `Contact: ${quotationData.client_info?.contact || ''}`,
      `Email: ${quotationData.client_info?.email || ''}`,
      `GST/VAT: ${quotationData.client_info?.gst_vat || ''}`,
    ];

    clientInfo.forEach((line, index) => {
      if (line && line !== 'undefined') {
        addText(line, 15, billY + (index * 5));
      }
    });

    // Quotation Details
    pdf.setFont('helvetica', 'bold');
    addText('Quotation Details', 110, detailsStartY);
    pdf.setFont('helvetica', 'normal');
let detailsY = detailsStartY + 7;
    // Safely format dates
    const formatDate = (dateStr) => {
      try {
        return new Date(dateStr).toLocaleDateString('en-IN');
      } catch {
        return dateStr || 'N/A';
      }
    };

    const quotationInfo = [
      [`Quotation #:`, quotationData.quotation_number || 'Auto-generate'],
      [`Date:`, formatDate(quotationData.date)],
      [`Valid Until:`, formatDate(quotationData.valid_till)],
      [`Currency:`, quotationData.currency || 'INR'],
    ];
    
    quotationInfo.forEach(([label, value], index) => {
      addText(label, 115, detailsY + (index * 5));
      addText(value, 150, detailsY + (index * 5));
    });

const leftHeight = clientInfo.length * 3 + 15;
const rightHeight = quotationInfo.length * 3 + 15;
yPos = 45 + Math.max(leftHeight, rightHeight) + 8;

    // Scope of Work
    if (quotationData.scope_of_work) {
      pdf.setFont('helvetica', 'bold');
      addText('Scope of Work:', 10, yPos);
      pdf.setFont('helvetica', 'normal');
      
      const scopeLines = pdf.splitTextToSize(
        quotationData.scope_of_work.replace(/[*\-•]/g, '').trim(), 
        180
      );
      
      // Check if we need a new page for scope
      checkPageBreak(scopeLines.length * 5 + 10);
      
      scopeLines.forEach((line, index) => {
        addText(line, 15, yPos + 5 + (index * 5));
      });
      
      yPos += (scopeLines.length * 5) + 10;
    }

    // Items Table with GST column - Check page break first
    checkPageBreak(50); // Reserve space for table header and a few rows
    
    pdf.setFont('helvetica', 'bold');
    addText("Items & Services", 10, yPos);
    yPos += 6;

    // Table Headers with GST column
    const headers = ['#', 'Description', 'Unit', 'Unit Price', 'Qty','SubTotal', 'CGST%', 'SGST%', 'Amt(inc. GST)'];
const colWidths = [10, 45, 15, 20, 15, 25, 15, 15, 30];
let xPos = 10; 
    pdf.setFillColor(41, 128, 185);
    pdf.setTextColor(255, 255, 255);
    pdf.rect(xPos, yPos, 190, 8, 'F');

    let headerX = xPos;
    headers.forEach((h, i) => {
      pdf.text(h, headerX + colWidths[i] / 2, yPos + 6, { align: 'center' });
      headerX += colWidths[i];
    });

    pdf.setTextColor(0, 0, 0);
    yPos += 8;

    // Rows with GST calculation
    quotationData.items?.forEach((item, index) => {
      // Check if we need a new page for this row
      if (checkPageBreak(10)) {
        // Re-add table headers on new page
        pdf.setFillColor(41, 128, 185);
        pdf.setTextColor(255, 255, 255);
        pdf.rect(10, yPos, 190, 8, 'F');
        
        let headerX = 10;
        headers.forEach((h, i) => {
          pdf.text(h, headerX + colWidths[i] / 2, yPos + 6, { align: 'center' });
          headerX += colWidths[i];
        });
        
        pdf.setTextColor(0, 0, 0);
        yPos += 8;
      }
      
      let rowX = 10;
 const itemSubtotal = (item.qty || 0) * (item.unit_price || 0);
  const cgstAmount = itemSubtotal * ((item.cgst || 0) / 100);
  const sgstAmount = itemSubtotal * ((item.sgst || 0) / 100);
  const amountWithGST = itemSubtotal + cgstAmount + sgstAmount;
      


// ===============================
// WRAPPED ROW CELLS (FIXED)
// ===============================

// Step 1: Split description into wrapped lines
let descriptionLines = pdf.splitTextToSize(
  String(item.description || "").replace(/[*\-•]/g, '').trim(),
  colWidths[1] - 4   // 4px padding
);

// Step 2: Calculate row height (wrap height or default)
let lineHeight = 6;
let rowHeight = Math.max(descriptionLines.length * lineHeight, 8);

let rowY = yPos;
let cellX = 10;

// Draw each cell properly
// 1. #
pdf.rect(cellX, rowY, colWidths[0], rowHeight);
addText(String(item.item_no || index + 1), cellX + 2, rowY + 5);
cellX += colWidths[0];

// 2. Description (wrapped)
pdf.rect(cellX, rowY, colWidths[1], rowHeight);
descriptionLines.forEach((line, i) => {
  addText(line, cellX + 2, rowY + 5 + (i * lineHeight));
});
cellX += colWidths[1];


// 4. Unit
pdf.rect(cellX, rowY, colWidths[2], rowHeight);
addText(String(item.unit || ''), cellX + 2, rowY + 5);
cellX += colWidths[2];

// 5. Unit Price
pdf.rect(cellX, rowY, colWidths[3], rowHeight);
addText(formatCurrency(item.unit_price || 0), cellX + 2, rowY + 5);
cellX += colWidths[3];
// 6. Qty
pdf.rect(cellX, rowY, colWidths[4], rowHeight);
addText(String(item.qty || 0), cellX + 2, rowY + 5);
cellX += colWidths[4];

  // 7. SubTotal
  pdf.rect(cellX, rowY, colWidths[5], rowHeight);
  addText(formatCurrency(itemSubtotal), cellX + 2, rowY + 5);
  cellX += colWidths[5];
// 7. CGST %
  pdf.rect(cellX, rowY, colWidths[6], rowHeight);
  addText(`${item.cgst || 0}%`, cellX + 2, rowY + 5);
  cellX += colWidths[6];

  // 8. SGST %
  pdf.rect(cellX, rowY, colWidths[7], rowHeight);
  addText(`${item.sgst || 0}%`, cellX + 2, rowY + 5);
  cellX += colWidths[7];

  // 9. Amount incl GST
  pdf.rect(cellX, rowY, colWidths[8], rowHeight);
  addText(formatCurrency(amountWithGST), cellX + 2, rowY + 5);

// Increase Y based on wrapped height
yPos += rowHeight;

    });

    // Additional Costs - Check page break
    checkPageBreak(50);
    
    yPos += 10;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    addText('Additional Costs:', 10, yPos);
    yPos += 5;

    pdf.setFont('helvetica', 'normal');

    // Table column widths
    const costLabelWidth = 120;
    const costValueWidth = 70;
    
    const additionalCosts = [
      ['Diagnostics/Evaluation Fee:', formatCurrency(parseFloat(quotationData.diagnostics_fee || 0))],
      ['Labour & Repair Cost:', formatCurrency(parseFloat(quotationData.labour_cost || 0))],
      ['Parts Estimate:', formatCurrency(parseFloat(quotationData.parts_estimate || 0))],
      ['Testing & Commissioning:', formatCurrency(parseFloat(quotationData.testing_and_commissioning || 0))],
      ['Packaging & Shipping:', formatCurrency(parseFloat(quotationData.packaging_and_shipping || 0))],
    ];

    additionalCosts.forEach(([label, value]) => {
      // Check page break for each row
      if (checkPageBreak(10)) {
        yPos += 5;
      }
      
      // Draw row box
      pdf.rect(10, yPos, costLabelWidth, 8);          // Label cell
      pdf.rect(10 + costLabelWidth, yPos, costValueWidth, 8); // Value cell

      addText(label, 12, yPos + 5);
      addText(value, 10 + costLabelWidth + costValueWidth - 5, yPos + 5, { align: 'right' });

      yPos += 8;
    });

// Build summary data
const summary = [
  ['Items Total:', formatCurrency(totals.itemsTotal)],
  ['Additional Costs:', formatCurrency(totals.additionalCosts)],
  ['Subtotal:', formatCurrency(totals.subtotal)],
];

if (quotationData.discount_percent > 0) {
  summary.push([`Discount (${quotationData.discount_percent}%):`, `-${formatCurrency(totals.discountAmount)}`]);
  summary.push(['After Discount:', formatCurrency(totals.afterDiscount)]);
}

summary.push(['Total CGST:', formatCurrency(totals.cgstTotal)]);
summary.push(['Total SGST:', formatCurrency(totals.sgstTotal)]);
summary.push(['Total GST:', formatCurrency(totals.gstTotal)]);

summary.push(['GRAND TOTAL:', formatCurrency(totals.grandTotal)]);

// ---- FIX: Check page break BEFORE drawing summary ----
const summaryHeight = (summary.length * 8) + 20; 
checkPageBreak(summaryHeight);

yPos += 10;

pdf.setFont('helvetica', 'bold');
addText('Summary:', 10, yPos);
yPos += 6;

// Table layout
const summaryLabelWidth = 120;
const summaryValueWidth = 70;

pdf.rect(10, yPos, summaryLabelWidth + summaryValueWidth, summary.length * 8);

summary.forEach(([label, value], i) => {
  const rowY = yPos + (i * 8);

  pdf.rect(10, rowY, summaryLabelWidth, 8);
  pdf.rect(10 + summaryLabelWidth, rowY, summaryValueWidth, 8);

  if (label === 'GRAND TOTAL:') {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(41, 128, 185);
  } else {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
  }

  addText(label, 12, rowY + 5);
  addText(value, 10 + summaryLabelWidth + summaryValueWidth - 5, rowY + 5, { align: 'right' });

  pdf.setTextColor(0, 0, 0);
});

yPos += summary.length * 8 + 15;

// --- SUMMARY SECTION END ---


    // Terms & Conditions - Always check page break
    checkPageBreak(50);
    
    pdf.setFont('helvetica', 'bold');
    addText('Terms & Conditions:', 10, yPos);
    pdf.setFont('helvetica', 'normal');
    yPos += 5;

    const terms = [
      ['Warranty:', `${quotationData.warranty?.months || 0} months`],
      ['Lead Time:', `${quotationData.lead_time_days || 0} days`],
      ['Payment Terms:', quotationData.payment_terms || ''],
      ['Delivery Terms:', quotationData.delivery_terms || ''],
    ];

    terms.forEach(([label, value]) => {
      // Check page break for each term
      if (checkPageBreak(20)) {
        yPos += 5;
      }
      
      addText(label, 15, yPos);
      const lines = pdf.splitTextToSize(
        String(value).replace(/[*\-•]/g, '').trim(), 
        130
      );
      lines.forEach((line, index) => {
        addText(line, 60, yPos + (index * 5));
      });
      yPos += (lines.length * 5) + 5;
    });

    // Notes
    if (quotationData.notes) {
      // Check page break for notes
      checkPageBreak(30);
      
      pdf.setFont('helvetica', 'bold');
      addText('Notes:', 10, yPos);
      pdf.setFont('helvetica', 'normal');
      yPos += 5;
      
      const noteLines = pdf.splitTextToSize(
        String(quotationData.notes).replace(/[*\-•]/g, '').trim(), 
        180
      );
      noteLines.forEach((line, index) => {
        addText(line, 15, yPos + (index * 5));
      });
    }

    // Save the PDF with proper filename
// Get today's date in DD_MM_YYYY format
const now = new Date();
const day = String(now.getDate()).padStart(2, '0');
const month = String(now.getMonth() + 1).padStart(2, '0');
const year = now.getFullYear();

const todayFormatted = `${day}_${month}_${year}`;

// Build file name
const fileName = `Quotation_${quotationData.quotation_number || 'Draft'}_${todayFormatted}.pdf`;
    pdf.save(fileName);

    setLoading(false);
  } catch (err) {
    //console.error('PDF generation error:', err);
    setError('Failed to generate PDF. Please try again.');
    setLoading(false);
  }
};

  // Generate PDF from HTML (alternative method using html2canvas)
  const generatePDFFromHTML = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const element = document.getElementById('quotation-preview');
      if (!element) {
        throw new Error('Preview element not found');
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      const fileName = `Quotation_${quotationData.quotation_number || 'Draft'}.pdf`;
      pdf.save(fileName);

      setLoading(false);
    } catch (err) {
      //console.error('HTML to PDF error:', err);
      setError('Failed to generate PDF from preview. Please try the direct PDF generation.');
      setLoading(false);
    }
  };

  // Direct print
  const handlePrint = () => {
    window.print();
  };

  if (!quotationData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdf color="primary" />
          <Typography variant="h6">Export Quotation as PDF</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Preview Section */}
          <Grid item xs={12} lg={8}>
            <Paper elevation={3} sx={{ p: 0, overflow: 'hidden' }}>
              <Box id="quotation-preview" sx={{ p: 4, bgcolor: 'white' }}>
                {/* Header */}
                <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 3, mb: 3, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight="bold">QUOTATION</Typography>
                  <Typography variant="subtitle1">Professional Repair & Retrofitting Services</Typography>
                </Box>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Quotation Details */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">Quotation Details</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1 }}>
                          <Typography fontWeight="bold">Quotation #:</Typography>
                          <Typography>{quotationData.quotation_number || 'Auto-generate'}</Typography>
                          <Typography fontWeight="bold">Date:</Typography>
                          <Typography>{new Date(quotationData.date).toLocaleDateString()}</Typography>
                          <Typography fontWeight="bold">Valid Until:</Typography>
                          <Typography>{new Date(quotationData.valid_till).toLocaleDateString()}</Typography>
                          <Typography fontWeight="bold">Currency:</Typography>
                          <Typography>{quotationData.currency}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

              
                </Grid>

                {/* Company & Client Details with GST */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business /> Company Details
                          </Box>
                        </Typography>
                        <Typography fontWeight="bold">{quotationData.company_info?.name}</Typography>
                        <Typography variant="body2">{quotationData.company_info?.address}</Typography>
                        <Typography variant="body2">Contact: {quotationData.company_info?.contact}</Typography>
                        <Typography variant="body2">Email: {quotationData.company_info?.email}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          GST/VAT: {quotationData.company_info?.gst_vat || 'Not Provided'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person /> Bill To
                          </Box>
                        </Typography>
                        <Typography fontWeight="bold">{quotationData.client_info?.name}</Typography>
                        <Typography variant="body2">{quotationData.client_info?.address}</Typography>
                        <Typography variant="body2">Contact: {quotationData.client_info?.contact}</Typography>
                        <Typography variant="body2">Email: {quotationData.client_info?.email}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          GST/VAT: {quotationData.client_info?.gst_vat || 'Not Provided'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Scope of Work */}
                {quotationData.scope_of_work && (
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">Scope of Work</Typography>
                      <Typography>{quotationData.scope_of_work}</Typography>
                    </CardContent>
                  </Card>
                )}

       {/* Items Table with CGST/SGST Columns */}
<Card variant="outlined" sx={{ mb: 3 }}>
  <CardContent>
    <Typography variant="h6" gutterBottom color="primary">Items & Services (with CGST/SGST)</Typography>
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f0f0' }}>
            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>#</th>
            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Description</th>
            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Unit</th>
            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Unit Price</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Qty</th>
            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>SubTotal</th>

            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>CGST %</th>
            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>SGST %</th>
            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Amount (incl. GST)</th>
          </tr>
        </thead>
        <tbody>
          {quotationData.items?.map((item) => {
            const itemSubtotal = (item.qty || 0) * (item.unit_price || 0);
            const cgstAmount = itemSubtotal * ((item.cgst || 0) / 100);
            const sgstAmount = itemSubtotal * ((item.sgst || 0) / 100);
            const amountWithGST = itemSubtotal + cgstAmount + sgstAmount;
            
            return (
              <tr key={item.item_no} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px' }}>{item.item_no}</td>
                <td style={{ padding: '10px' }}>{item.description}</td>
                <td style={{ padding: '10px', textAlign: 'center' }}>{item.unit}</td>
                <td style={{ padding: '10px', textAlign: 'right' }}>{formatCurrency(parseFloat(item.unit_price || 0))}</td>
                                <td style={{ padding: '10px', textAlign: 'center' }}>{item.qty}</td>
                <td style={{ padding: '10px', textAlign: 'center' }}>{formatCurrency(parseFloat(itemSubtotal))}</td>

                <td style={{ padding: '10px', textAlign: 'center' }}>{item.cgst || 0}%</td>
                <td style={{ padding: '10px', textAlign: 'center' }}>{item.sgst || 0}%</td>
                <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                  {formatCurrency(amountWithGST)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  </CardContent>
</Card>
                {/* Additional Costs */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">Additional Costs</Typography>
                    <Grid container spacing={2}>
                      {quotationData.diagnostics_fee > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography>Diagnostics/Evaluation Fee:</Typography>
                            <Typography>{formatCurrency(parseFloat(quotationData.diagnostics_fee || 0))}</Typography>
                          </Box>
                        </Grid>
                      )}
                      {quotationData.labour_cost > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography>Labour & Repair Cost:</Typography>
                            <Typography>{formatCurrency(parseFloat(quotationData.labour_cost || 0))}</Typography>
                          </Box>
                        </Grid>
                      )}
                      {quotationData.parts_estimate > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography>Parts Estimate:</Typography>
                            <Typography>{formatCurrency(parseFloat(quotationData.parts_estimate || 0))}</Typography>
                          </Box>
                        </Grid>
                      )}
                      {quotationData.testing_and_commissioning > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography>Testing & Commissioning:</Typography>
                            <Typography>{formatCurrency(parseFloat(quotationData.testing_and_commissioning || 0))}</Typography>
                          </Box>
                        </Grid>
                      )}
                      {quotationData.packaging_and_shipping > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography>Packaging & Shipping:</Typography>
                            <Typography>{formatCurrency(parseFloat(quotationData.packaging_and_shipping || 0))}</Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Summary Section with GST Breakdown */}
                <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">Summary with GST</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Items Total:</Typography>
                        <Typography>{formatCurrency(totals.itemsTotal)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Additional Costs:</Typography>
                        <Typography>{formatCurrency(totals.additionalCosts)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #ddd', pt: 1 }}>
                        <Typography fontWeight="bold">Subtotal:</Typography>
                        <Typography fontWeight="bold">{formatCurrency(totals.subtotal)}</Typography>
                      </Box>
                      
                      {quotationData.discount_percent > 0 && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'success.main' }}>
                            <Typography>Discount ({quotationData.discount_percent}%):</Typography>
                            <Typography>-{formatCurrency(totals.discountAmount)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography>After Discount:</Typography>
                            <Typography>{formatCurrency(totals.afterDiscount)}</Typography>
                          </Box>
                        </>
                      )}
                      
                         <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#e8f4fd', p: 1, borderRadius: 1 }}>
        <Typography fontWeight="bold">Total CGST:</Typography>
        <Typography fontWeight="bold">{formatCurrency(totals.cgstTotal)}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#e8f4fd', p: 1, borderRadius: 1 }}>
        <Typography fontWeight="bold">Total SGST:</Typography>
        <Typography fontWeight="bold">{formatCurrency(totals.sgstTotal)}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#f0f0f0', p: 1, borderRadius: 1 }}>
        <Typography variant="h6" fontWeight="bold">Total GST:</Typography>
        <Typography variant="h6" fontWeight="bold">{formatCurrency(totals.gstTotal)}</Typography>
      </Box>
                      
                    
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.light', p: 2, borderRadius: 1 }}>
                        <Typography variant="h5" fontWeight="bold" color="primary">GRAND TOTAL</Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary">{formatCurrency(totals.grandTotal)}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Terms & Conditions */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">Terms & Conditions</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2">Warranty</Typography>
                        <Typography>{quotationData.warranty?.months || 0} months</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {quotationData.warranty?.terms}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2">Lead Time</Typography>
                        <Typography>{quotationData.lead_time_days || 0} days</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Payment Terms</Typography>
                        <Typography>{quotationData.payment_terms}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Delivery Terms</Typography>
                        <Typography>{quotationData.delivery_terms}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Notes */}
                {quotationData.notes && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">Additional Notes</Typography>
                      <Typography>{quotationData.notes}</Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Footer */}
                <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ddd', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Generated by AI Quotation Generator • This is a computer-generated document
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={generatePDF}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotationPDFExport;